// Screen-space room plaques: crisp at any zoom (the 3D banners were ~6 px tall at the default
// camera). Positions are written straight to the DOM on every board frame — no React re-render.
// Each plaque shows the room's icon, name and what it gives; details stay in the hover popup.
import { useEffect, useRef } from 'react';
import { LOCATION_IDS, type LocationId, type PlayerView } from '@bcm/rules';
import type { BoardScene } from '../scene/BoardScene';
import { LOCATION_ICON, type IconName } from '../game/icons';
import { C, SEAL_COLORS, locationCapacity, resolutionInForce, roomOpen } from '../game/content';
import { RichText } from './Card';
import { Icon } from './Icon';

const SHORT: Record<LocationId, { name: string; gives: string }> = {
  conjuration: { name: 'Conjuration', gives: '{R}' },
  transmutation: { name: 'Transmutation', gives: '{Y}' },
  evocation: { name: 'Evocation', gives: '{B}{B}' },
  divination: { name: 'Divination', gives: '{G}{G}' },
  enchantment: { name: 'Enchantment', gives: '{B}{G}' },
  procuration: { name: 'Procuration', gives: '' },
  'mana-conduit': { name: 'Mana Conduit', gives: '{MANA}' },
  arcanium: { name: 'Arcanium', gives: '' },
  archive: { name: 'Archive', gives: '' },
  'planning-office': { name: 'Planning Office', gives: '' },
  'grand-auditor': { name: 'Grand Auditor', gives: '' },
  precedence: { name: 'Precedence', gives: '' },
  // Annex rooms (P-18).
  'exchange-bureau': { name: 'Exchange Bureau', gives: '' },
  'lost-property': { name: 'Lost Property', gives: '' },
  'shredding-room': { name: 'Shredding Room', gives: '' },
  'stamp-pound': { name: 'Stamp Pound', gives: '' },
  'complaints-desk': { name: 'Complaints Desk', gives: '{MANA}' },
  mailroom: { name: 'Mailroom', gives: '' },
};

/**
 * What a room gives, with the Resolutions in force folded in (P-17): the one line a player reads
 * to know the room changed (Storm Power's mana at Evocation, the Night Market at the Conduit…).
 */
function givesFor(view: PlayerView, loc: LocationId): { gives: string; badge: { icon: IconName; tip: string } | null } {
  let gives = SHORT[loc].gives;
  let badge: { icon: IconName; tip: string } | null = null;
  const on = (id: string) => resolutionInForce(view, id);
  if (loc === 'evocation' && on('resolution.storm_power')) gives += '{MANA}';
  if (loc === 'precedence' && on('resolution.ceremonial_stipend')) gives += '{MANA}';
  if (loc === 'mana-conduit' && on('resolution.night_market')) badge = { icon: 'potion', tip: 'Night Market|After the mana, you may buy 1 spell from the Arcanium display.' };
  if (C.locations.get(loc)?.keyword === 'department' && on('resolution.magical_weather')) badge = { icon: 'coin', tip: 'Magical Weather|Each visit flips the Ministry coin. Heads: the room resolves again.' };
  if (loc === 'arcanium' && on('resolution.open_shelves')) badge = { icon: 'book', tip: 'Open Shelves|Refreshing the display is free.' };
  return { gives, badge };
}

export function RoomLabels({ scene, view, onClick }: { scene: BoardScene; view: PlayerView; onClick: (loc: LocationId) => void }) {
  const refs = useRef(new Map<LocationId, HTMLButtonElement>());
  /** The last press on a label was a finger (tap to inspect, tap again to act). */
  const touchRef = useRef(false);
  // Label sizes, cached: reading offsetWidth every frame would force a layout per frame.
  const sizes = useRef(new Map<Element, { w: number; h: number }>());
  // Labels can appear mid-game (an Annex room opening), so each one is observed as it mounts.
  const observer = useRef<ResizeObserver | null>(null);
  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) sizes.current.set(e.target, { w: (e.target as HTMLElement).offsetWidth, h: (e.target as HTMLElement).offsetHeight });
    });
    observer.current = ro;
    for (const el of refs.current.values()) ro.observe(el);
    return () => {
      ro.disconnect();
      observer.current = null;
    };
  }, []);
  const last = useRef(new Map<Element, string>());

  useEffect(() => {
    // The canvas fills the board; its rect only changes on resize.
    let cached = scene.renderer.domElement.getBoundingClientRect();
    const onResize = () => (cached = scene.renderer.domElement.getBoundingClientRect());
    addEventListener('resize', onResize);
    const rect = () => cached;
    // Runs after each frame the board draws (it only moves when the camera does).
    const tick = () => {
      const r = rect();
      const anyActive = LOCATION_IDS.some((l) => {
        if (!scene.roomReady(l)) return false;
        const x = scene.highlightOf(l);
        return x && x !== 'dim';
      });
      // Project, then resolve overlaps greedily (top to bottom): a label that would sit on one
      // already placed moves down (or up) just enough to clear it.
      const items = LOCATION_IDS.flatMap((loc) => {
        const el = refs.current.get(loc);
        if (!el) return [];
        const p = scene.screenPos(scene.labelAnchor(loc));
        const size = sizes.current.get(el) ?? { w: 150, h: 26 };
        return [{ loc, el, x: p.x - r.left, y: p.y - r.top, w: size.w, h: size.h }];
      });
      items.sort((a, b) => a.y - b.y || a.x - b.x);
      const placed: { x: number; y: number; w: number; h: number }[] = [];
      for (const it of items) {
        for (let tries = 0; tries < 4; tries++) {
          const hit = placed.find((p) => Math.abs(p.x - it.x) < (p.w + it.w) / 2 + 4 && Math.abs(p.y - it.y) < (p.h + it.h) / 2 + 2);
          if (!hit) break;
          it.y = it.y >= hit.y ? hit.y + (hit.h + it.h) / 2 + 3 : hit.y - (hit.h + it.h) / 2 - 3;
        }
        placed.push(it);
        const { el, loc } = it;
        const tf = `translate(${Math.round(it.x)}px, ${Math.round(it.y)}px) translate(-50%, -50%)`;
        if (last.current.get(el) !== tf) {
          el.style.transform = tf;
          last.current.set(el, tf);
        }
        const h = scene.highlightOf(loc);
        const state = h && h !== 'dim' ? `on-${h}` : anyActive ? 'off' : '';
        if (el.dataset.state !== state) el.dataset.state = state;
        const hov = scene.isHovered(loc) ? '1' : '';
        if (el.dataset.hover !== hov) el.dataset.hover = hov;
        // An Annex room's label appears once its construction is done (P-18).
        const hidden = scene.roomReady(loc) ? '' : '1';
        if (el.dataset.hidden !== hidden) el.dataset.hidden = hidden;
      }
    };
    tick();
    const off = scene.onFrame(tick);
    return () => {
      off();
      removeEventListener('resize', onResize);
    };
  }, [scene]);

  return (
    <div className="room-labels" aria-label="Rooms">
      {LOCATION_IDS.map((loc) => {
        if (!view.board[loc] || !roomOpen(view, loc)) return null; // closed (an Annex bay not open), or a match saved before it existed
        const blocked = view.board[loc].blockedBy.length > 0;
        const { gives, badge } = givesFor(view, loc);
        const cap = locationCapacity(view, loc);
        const occ = view.board[loc].occupants;
        const seats = occ.map((a) => Number(a.slice(1, a.indexOf('a'))));
        const slots = Math.max(cap, occ.length);
        const full = occ.length >= cap;
        return (
          <button
            key={loc}
            ref={(el) => {
              if (el) {
                // Inline refs run on every render: observe each element only once.
                if (!sizes.current.has(el)) observer.current?.observe(el);
                refs.current.set(loc, el);
              } else refs.current.delete(loc);
            }}
            className={`room-label ${blocked ? 'blocked' : ''} ${full ? 'full' : ''}`}
            onPointerDown={(e) => (touchRef.current = e.pointerType === 'touch')}
            onClick={() => (touchRef.current ? scene.tapRoom(loc) : onClick(loc))}
            onPointerEnter={(e) => e.pointerType !== 'touch' && scene.hoverRoom(loc)}
            onPointerLeave={(e) => e.pointerType !== 'touch' && scene.hoverRoom(null)}
            aria-label={`${SHORT[loc].name}: ${occ.length} of ${cap} places taken`}
          >
            <span className="rl-medal">
              <Icon name={LOCATION_ICON[loc]} size={14} />
            </span>
            <span className="rl-name">{SHORT[loc].name}</span>
            {(blocked || gives) && <span className="rl-gives">{blocked ? 'Audited' : <RichText text={gives} />}</span>}
            {badge && !blocked && (
              <span className="rl-badge" data-tip={badge.tip}>
                <Icon name={badge.icon} size={11} />
              </span>
            )}
            <span className="rl-slots" aria-hidden>
              {Array.from({ length: slots }, (_, i) => (
                <span key={i} className={`rl-slot ${i < seats.length ? 'taken' : ''}`} style={i < seats.length ? { background: SEAL_COLORS[seats[i]!] } : undefined} />
              ))}
            </span>
          </button>
        );
      })}
    </div>
  );
}
